import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  flagsFromMeasure,
  fmtAgeSince,
  fmtCount,
  fmtMoneyCompact,
  fmtMs,
  fmtShortDate,
  type AlertRule,
  type CaptureSummary,
  type FacilityVitals,
  type FleetFacility,
  type EnabledFrameworks,
  type FrameworksResponse,
  type MetricsMonitoring,
  type MetricsOverview,
  type OperatorFleet,
  type PolicyPosture,
  type PulseAnomalies,
  type PulseFacilities,
  type PulseIncident,
  type PulseIncidents,
  type PulseSlo,
  type SigStatus,
  type SignalName,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import type { QueryState } from '../../useQuery';
import { HonestyBadge } from '../../ui/HonestyBadge';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { EmptyState, QueryBlock, Skeleton } from '../../ui/states';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { initials, shortName } from './common';
import { ackerName } from '../pulse/acker';
import { useBuildText, DevNote, useBuildLabels, useCustomerText } from '../../ui/buildLabels';

// Every block here is a port of the Console V3 design's Operator overview (the ov* keys
// in "Aithora ConsoleV3.dc.html"); class names map to its inline styles in operator.css.
// Figures come only from the routes. Where a route does not serve something yet, the
// block says so instead of drawing the design's sample number.

/** The design's section heading: 13px name, then an 11.5px muted meta line. */
function Head({ title, meta, small, wrap, children }: { title: string; meta?: ReactNode; small?: boolean; wrap?: boolean; children?: ReactNode }) {
  return (
    <div className={`op-head${wrap ? ' op-head--wrap' : ''}`}>
      <h3 className={`op-head__title${small ? ' op-head__title--small' : ''}`}>{title}</h3>
      {meta !== undefined && <span className="op-head__meta">{meta}</span>}
      {children}
    </div>
  );
}

/** Median as the design takes it: the upper middle value. */
export function upperMedian(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

// ── Facilities ──────────────────────────────────────────────────────────────

type Tone = 'good' | 'watch' | 'act';

/** Banded against the peer group (design): 80 and up strong, at or above the median watch, below it attention. */
function scoreTone(score: number, median: number): Tone {
  return score >= 80 ? 'good' : score >= median ? 'watch' : 'act';
}

/** Worst composite first; a facility with no composite goes last. Ties keep the route's order. */
export function worstFirst(rows: FleetFacility[]): FleetFacility[] {
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const av = a.r.score.value;
      const bv = b.r.score.value;
      if (av === null && bv === null) return a.i - b.i;
      if (av === null) return 1;
      if (bv === null) return -1;
      return av - bv || a.i - b.i;
    })
    .map((x) => x.r);
}

/** Facilities below the peer median, from the same composites the block draws. */
export function belowMedianCount(rows: FleetFacility[]): number {
  const vals = rows.map((r) => r.score.value).filter((v): v is number => v !== null);
  const med = upperMedian(vals);
  return vals.filter((v) => v < med).length;
}

export function FacilitiesBlock({ q, shown = 6 }: { q: QueryState<OperatorFleet>; shown?: number }) {
  const plain = useCustomerText();
  const d = q.data;
  const build = useBuildLabels();
  const graded = d ? d.facilities.filter((f) => f.score.value !== null).length : 0;
  const hasTrend = d ? d.facilities.some((f) => f.trend && f.trend.value !== null) : false;
  const trendFlag = d?.facilities.find((f) => f.trend)?.trend;
  return (
    <section className="op-fac" aria-labelledby="op-fac-title">
      <div className="op-head op-head--wrap op-fac__head">
        <h3 className="op-head__title" id="op-fac-title">
          Facilities
        </h3>
        {d && (
          <span className="op-head__meta op-head__meta--full">
            {`${fmtCount(graded)} graded · worst first · CMS composite${hasTrend ? (build ? ` · trend ${(flagsFromMeasure(trendFlag)?.basis ?? 'measured').toUpperCase()}` : '') : build ? plain(' · trend not served yet', ' · trend not available yet') : ''}`}
            <HonestyBadge flags={[flagsFromMeasure(d.facilities.find((f) => f.score.value !== null)?.score)]} figure="Facilities: CMS composite" hide={['MEASURED', 'MODELED', 'ESTIMATED']} />
          </span>
        )}
      </div>
      <QueryBlock q={q} rows={3} isEmpty={(x) => x.facilities.length === 0} empty={<EmptyState title="No facilities yet" body="Facilities appear here once they are added in Settings." compact />}>
        {(x) => {
          const vals = x.facilities.map((r) => r.score.value).filter((v): v is number => v !== null);
          const med = upperMedian(vals);
          const lo = vals.length ? Math.min(...vals) : 0;
          const span = (vals.length ? Math.max(...vals) : 100) - lo || 1;
          const rows = worstFirst(x.facilities).slice(0, shown);
          return (
            <div className="op-fac__grid">
              {rows.map((f) => {
                const c = f.score.value;
                const tone: Tone | 'none' = c === null ? 'none' : scoreTone(c, med);
                const t = f.trend?.value ?? null;
                const dt = t === null ? null : Math.round(t);
                return (
                  <Link
                    key={f.orgId}
                    to="/performance"
                    className={`op-fac__row op-edge--${tone}`}
                    title={`${f.name} · ${c === null ? 'no composite' : `composite ${Math.round(c)} vs typical for peers ${med}`}`}
                  >
                    <i className={`op-fac__fill op-fill--${tone}`} style={{ width: c === null ? '0%' : `${Math.round(((c - lo) / span) * 100)}%` }} aria-hidden="true" />
                    <span className="op-fac__name">{shortName(f.name)}</span>
                    <span className="op-fac__score">{c === null ? (f.grade ?? '–') : Math.round(c)}</span>
                    {dt !== null && (
                      <span className={`op-fac__delta op-ink--${dt === 0 ? 'none' : dt > 0 ? 'good' : 'act'}`}>{dt === 0 ? 'flat' : `${dt > 0 ? '▲' : '▼'} ${Math.abs(dt)}`}</span>
                    )}
                  </Link>
                );
              })}
              <Link to="/performance" className="op-more">
                {`See all ${fmtCount(x.facilities.length)} facilities →`}
              </Link>
            </div>
          );
        }}
      </QueryBlock>
    </section>
  );
}

// ── Money on the table ──────────────────────────────────────────────────────

export interface MoneyRow {
  facility: FleetFacility;
  summary: CaptureSummary;
}

/**
 * Soonest filing window first when the route sends windows (ties keep the fleet's own
 * order, as the design's do); otherwise worst composite first. `rows` is in fleet order.
 */
export function moneyOrder(rows: MoneyRow[]): MoneyRow[] {
  const withWindow = rows.length > 0 && rows.every((r) => r.summary.filingWindow);
  if (!withWindow) {
    const rank = new Map(worstFirst(rows.map((r) => r.facility)).map((f, i) => [f.orgId, i]));
    return [...rows].sort((a, b) => rank.get(a.facility.orgId)! - rank.get(b.facility.orgId)!);
  }
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => a.r.summary.filingWindow!.closesInDays - b.r.summary.filingWindow!.closesInDays || a.i - b.i)
    .map((x) => x.r);
}

function windowEdge(days: number | undefined): string {
  if (days === undefined) return 'op-money__row--none';
  return days <= 10 ? 'op-money__row--act' : days <= 16 ? 'op-money__row--watch' : 'op-money__row--none';
}

/**
 * A building's money on the table: Capture's "Not billed" (care documented this month minus
 * what the practitioner billed; the modeled opportunity with nothing documented), served on
 * the capture summary so Overview, Capture and Finance print one figure.
 */
export function notBilledOf(s: CaptureSummary): CaptureSummary['recoverable'] {
  return s.notBilled ?? { value: null, unit: 'cents', basis: 'measured', source: 'Not served by this back end yet.', asOf: null };
}

export function MoneyBlock({ q, network }: { q: QueryState<MoneyRow[]>; network: QueryState<CaptureSummary> }) {
  const dollars = (f: CaptureSummary['recoverable'] | undefined) => (f && f.value !== null ? f.value / 100 : null);
  return (
    <section className="op-money" aria-label="Money on the table">
      <div className="op-head op-money__head">
        <h3 className="op-head__title">Money on the table</h3>
      </div>
      <QueryBlock
        q={q}
        rows={3}
        isEmpty={(d) => d.length === 0}
        empty={<EmptyState title="No capture figures yet" body="Not-billed dollars appear once a facility's care is documented." compact />}
      >
        {(rows) => (
          <span className="op-money__grid">
            {rows.map(({ facility, summary }) => {
              const days = summary.filingWindow?.closesInDays;
              return (
                <Link
                  key={facility.orgId}
                  to="/capture"
                  className={`op-money__row ${windowEdge(days)}`}
                  title={`${shortName(facility.name)} · not billed this month${days !== undefined ? ` · closes ${days}d` : ''} · open in Capture`}
                >
                  <span className="op-money__name">{shortName(facility.name)}</span>
                  <span className="op-money__amt">{fmtMoneyCompact(dollars(notBilledOf(summary)))}</span>
                  <HonestyBadge flags={[{ real: summary.real }, { basis: notBilledOf(summary).basis }]} figure="Money: not billed" className="ov-badge-mini" hide={['MEASURED', 'MODELED', 'ESTIMATED']} />
                  <span className="op-money__flag" aria-hidden="true">
                    {'⚑'}
                  </span>
                </Link>
              );
            })}
          </span>
        )}
      </QueryBlock>
      <div className="op-note op-money__note">
        <MoneyNote network={network} />
      </div>
    </section>
  );
}

/** The line under Money on the table (design ovAgingLine), also under the To do's Money column. */
export function MoneyNote({ network }: { network: QueryState<CaptureSummary> }) {
  const bt = useBuildText();
  const n = network.data;
  const dollars = (f: CaptureSummary['recoverable'] | undefined) => (f && f.value !== null ? f.value / 100 : null);
  return (
    <>
        {n ? (
          n.engine && n.denialRate ? (
            <>
              {bt(`Engine ${n.engine.live ? 'live' : 'not live'} · ${fmtCount(n.engine.connectedFacilities)} ${n.engine.connectedFacilities === 1 ? 'facility' : 'facilities'} connected · dollars, windows and denials ${
                n.recoverable.basis === 'measured' ? 'MEASURED' : 'MODELED'
              } · ${n.denialRate.pct}% denial rate (${n.denialRate.source}) · ${fmtMoneyCompact(dollars(n.recoverable) === null ? null : (dollars(n.recoverable)! * n.denialRate.pct) / 100)} denials · root cause and AR 90+ pending the RCM rollup`)}
              <HonestyBadge flags={[{ real: n.real }]} figure="Money: network" hide={['MEASURED']} />
            </>
          ) : (
            <>
              Recoverable {fmtMoneyCompact(dollars(n.recoverable))} <HonestyBadge flags={[{ real: n.real }, { basis: n.recoverable.basis }]} figure="Money: network recoverable" /> {'·'} opportunity{' '}
              {fmtMoneyCompact(dollars(n.opportunity))} <HonestyBadge flags={[{ real: n.real }, { basis: n.opportunity.basis }]} figure="Money: opportunity" /> {'·'} never added together {'·'} denial rate
              and filing windows {n.denialRisk.reason ? `not live: ${n.denialRisk.reason}` : 'not live yet'}
            </>
          )
        ) : network.error ? (
          'Network capture totals could not load.'
        ) : (
          ' '
        )}
    </>
  );
}

// ── Needs action tiles ──────────────────────────────────────────────────────

export type TileSev = 'breach' | 'risk' | 'normal';
export interface Tile {
  key: string;
  short: string;
  long: string;
  n: number;
  /** Printed instead of n when the source sent a word ("!", "$12K"). */
  text?: string;
  /** The source marked this figure modeled (a catalog rate standing in for a claim). */
  modeled?: boolean;
  sev: TileSev;
  to: string;
}

/** The design's ramp: only a breach tile that is hot within its band goes solid. */
function tileClass(t: Tile, all: Tile[]): string {
  if (t.sev !== 'breach' || t.n <= 0) return `ov-tile ov-tile--${t.sev}`;
  const vals = all.filter((x) => x.sev === 'breach').map((x) => x.n);
  const hi = Math.max(...vals);
  const lo = Math.min(...vals);
  const f = hi === lo ? 0.6 : 0.35 + ((t.n - lo) / (hi - lo)) * 0.55;
  return `ov-tile ov-tile--breach${f > 0.5 ? ' ov-tile--loud' : ''}`;
}

/**
 * The tile grid's columns: `cols` across while each tile has room for its longest word
 * (104px), else as many 104px columns as fit. A label wraps between words, never inside one.
 */
export function tileColumns(cols: number): string {
  return `repeat(auto-fill, minmax(max(${TILE_MIN}px, calc((100% - ${(cols - 1) * 3}px) / ${cols})), 1fr))`;
}
export const TILE_MIN = 104;

export function NeedsTiles({
  tiles,
  title = 'Needs action',
  sub,
  badge,
  notCounted,
  loading,
  cols = 4,
}: {
  tiles: Tile[];
  title?: string;
  sub?: string;
  badge?: ReactNode;
  notCounted?: string[];
  loading?: boolean;
  cols?: number;
}) {
  const order = { breach: 0, risk: 1, normal: 2 } as const;
  const sorted = [...tiles].sort((a, b) => order[a.sev] - order[b.sev]);
  return (
    <section className="op-needs" aria-label={title}>
      <span className="op-needs__head">
        <h3 className="op-needs__title">{title}</h3>
        {sub && <span className="op-needs__title">{sub}</span>}
        {badge}
        <span className="spacer" />
        <span className="op-legend">
          <span>
            <i className="op-key op-key--breach" />
            breach
          </span>
          <span>
            <i className="op-key op-key--risk" />
            at risk
          </span>
          <span>
            <i className="op-key op-key--normal" />
            normal
          </span>
        </span>
      </span>
      {loading && tiles.length === 0 ? (
        <Skeleton rows={3} />
      ) : tiles.length === 0 ? (
        <EmptyState title="Nothing counted yet" body="The sources behind these tiles have not answered." compact />
      ) : (
        <span className="ov-tiles" style={{ gridTemplateColumns: tileColumns(cols) }}>
          {sorted.map((t) => (
            <Link key={t.key} to={t.to} className={tileClass(t, sorted)} title={t.long} data-testid={`tile-${t.key}`}>
              <span className="ov-tile__n">{t.text ?? fmtCount(t.n)}</span>
              <span className="ov-tile__k">
                {t.short}
                {t.modeled && <HonestyBadge flags={[{ modeled: true }]} figure={`Tile ${t.short}`} className="ov-badge-mini" />}
              </span>
            </Link>
          ))}
        </span>
      )}
      {notCounted && notCounted.length > 0 && <DevNote><p className="op-note">Not counted yet (no route): {notCounted.join(' · ')}</p></DevNote>}
    </section>
  );
}

// ── Governed volume ─────────────────────────────────────────────────────────

/**
 * The design's convBars: scaled to the second-highest day so one spike cannot flatten
 * the rest; a clipped bar is drawn in blue; a dashed line marks the median.
 */
/** The bars alone (V9 Governed volume draws them 64px tall under its own heading). */
export function VolumeBars({ days, H = 132 }: { days: MetricsOverview['conversations']['recentDays']; H?: number }) {
  const counts = days.map((d) => d.count);
  const peak = counts.length ? Math.max(...counts) : 0;
  const med = upperMedian(counts);
  const W = 1000;
  const mB = 4;
  const mT = 10;
  const ranked = [...counts].sort((a, b) => b - a);
  const scaleMax = Math.max(ranked[1] || 1, 1);
  const bw = (W / Math.max(1, days.length)) * 0.8;
  const gap = (W / Math.max(1, days.length)) * 0.2;
  const y = (v: number) => H - mB - (Math.min(v, scaleMax) / scaleMax) * (H - mB - mT);
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`Conversations per day, peak ${peak}`}>
      <line x1={0} x2={W} y1={y(med)} y2={y(med)} stroke="rgba(34,39,46,.3)" strokeWidth={1} strokeDasharray="4 4" />
      {days.map((d, i) => {
        const top = y(d.count);
        return (
          <rect key={d.date} x={i * (bw + gap) + gap / 2} y={top} width={bw} height={Math.max(1.5, H - mB - top)} rx={2} fill={d.count > scaleMax ? '#2F5C97' : '#4E9E7F'}>
            <title>{`${fmtShortDate(d.date)}: ${d.count}${d.backfill ? ' (filled in later)' : ''}`}</title>
          </rect>
        );
      })}
    </svg>
  );
}

export function VolumeChart({ q }: { q: QueryState<MetricsOverview> }) {
  const days = q.data?.conversations.recentDays ?? [];
  const counts = days.map((d) => d.count);
  const peak = counts.length ? Math.max(...counts) : 0;
  const peakDay = days.find((d) => d.count === peak);
  const avg = counts.length ? Math.round(counts.reduce((a, b) => a + b, 0) / counts.length) : 0;
  const med = upperMedian(counts);
  return (
    // Atlas conversations per day (the conversations table), not gate decisions: Guard posture's
    // Outcomes counts those (the ledger), so the two read different sources and say so.
    <section className="op-volume" aria-label="Atlas conversations">
      <Link to="/guardian" className="op-volume__head">
        <span className="op-volume__title">
          {`Atlas conversations · peak ${fmtCount(peak)}${peakDay?.backfill ? ' · filled in' : ''}`}
          {q.data && <HonestyBadge flags={[{ real: q.data.conversations.real }]} figure="Atlas conversations" className="ov-badge-mini" hide={['MEASURED']} />}
        </span>
        <span className="op-volume__go">Guardian {'→'}</span>
      </Link>
      <QueryBlock q={q} rows={2} isEmpty={(d) => d.conversations.recentDays.length === 0} empty={<EmptyState title="No governed conversations in the window" compact />}>
        {() => (
          <>
            <Link to="/guardian" title="Open Guardian" className="op-volume__chart">
              <VolumeBars days={days} />
            </Link>
            <span className="op-volume__axis">
              <span>{fmtShortDate(days[0]?.date)}</span>
              <span>{`typical ${fmtCount(med)} · daily average ${fmtCount(avg)}`}</span>
              <span>{fmtShortDate(days[days.length - 1]?.date)}</span>
            </span>
          </>
        )}
      </QueryBlock>
    </section>
  );
}

// ── Compliance ──────────────────────────────────────────────────────────────

/**
 * Compliance chips (design ovCompliance): each framework this tenant has switched on, MAPPED
 * (its policies are sealed on the gate: GET /api/frameworks/enabled), or the state the
 * framework list itself carries.
 */
export function ComplianceBlock({ q, enabled }: { q: QueryState<FrameworksResponse>; enabled?: QueryState<EnabledFrameworks> }) {
  const on = new Set(enabled?.data?.enabled ?? []);
  const mapped = q.data ? q.data.frameworks.filter((f) => f.state || on.has(f.id)).map((f) => ({ ...f, state: f.state ?? ('MAPPED' as const) })) : [];
  const noneOn = !!enabled?.data && on.size === 0 && mapped.length === 0;
  return (
    <section className="op-compliance" aria-label="Compliance">
      <span className="op-sub">Compliance</span>
      {mapped.length > 0 ? (
        <span className="op-chips">
          {mapped.map((f) => (
            <Link key={f.id} to="/settings" title="Open compliance settings" className="op-chip">
              {f.name}
              <span className={`op-chip__v${f.state === 'MAPPED' ? ' op-chip__v--ok' : ''}`}>{f.state}</span>
            </Link>
          ))}
        </span>
      ) : q.loading && !q.data ? (
        <Skeleton rows={1} />
      ) : noneOn ? (
        <EmptyState compact title="No compliance framework is switched on yet" body="Switch one on in Settings, Compliance, and it shows here as mapped." />
      ) : (
        <EmptyState
          compact
          title="Compliance status is not connected yet"
          waitingOn="a status for each framework (SOC 2, HIPAA, GDPR, EU AI Act)"
        />
      )}
    </section>
  );
}

// ── Open incidents ──────────────────────────────────────────────────────────

/** Unresolved only: unacknowledged first, then nearest to its escalation threshold (or oldest). */
export function rankIncidents(list: PulseIncident[], now: Date = new Date()): PulseIncident[] {
  const ratio = (i: PulseIncident) => (i.escalateAtMinutes ? (now.getTime() - Date.parse(i.openedAt)) / 60000 / i.escalateAtMinutes : null);
  return list
    .filter((i) => i.status !== 'resolved')
    .sort((a, b) => {
      const s = (a.status === 'open' ? 0 : 1) - (b.status === 'open' ? 0 : 1);
      if (s) return s;
      const ra = ratio(a);
      const rb = ratio(b);
      if (ra !== null && rb !== null) return rb - ra;
      return Date.parse(a.openedAt) - Date.parse(b.openedAt);
    });
}

/** An incident's second line (design ovIncidents barNote). */
export function incidentNote(i: PulseIncident, now: Date = new Date()): string {
  const held = i.status === 'acknowledged';
  const ageMin = (now.getTime() - Date.parse(i.openedAt)) / 60000;
  const pct = i.escalateAtMinutes ? Math.min(100, Math.round((ageMin / i.escalateAtMinutes) * 100)) : null;
  return i.escalateAtMinutes
    ? held
      ? `held before escalation at ${i.escalateAtMinutes}m`
      : `${pct}% to escalation at ${i.escalateAtMinutes}m`
    : held
      ? `acknowledged ${fmtAgeSince(i.acknowledgedAt, now)} ago, escalation halted`
      : i.escalationLevel > 0
        ? `escalated to level ${i.escalationLevel} · ${i.severity}`
        : `not escalated yet · ${i.severity}`;
}

export function IncidentsBlock({
  q,
  overview,
  rules,
  facilities,
  anomalies,
}: {
  q: QueryState<PulseIncidents>;
  overview: QueryState<MetricsOverview>;
  rules: QueryState<AlertRule[]>;
  facilities: QueryState<PulseFacilities>;
  anomalies: QueryState<PulseAnomalies>;
}) {
  const { client } = useSession();
  const roReason = useReadOnlyReason();
  const readOnly = roReason !== null;
  const [acking, setAcking] = useState<PulseIncident | null>(null);
  const now = new Date();
  const open = q.data ? rankIncidents(q.data.incidents, now) : [];
  const unacked = open.filter((i) => i.status === 'open').length;
  const off = rules.data ? rules.data.filter((r) => !r.enabled) : null;
  const neverEsc = facilities.data ? facilities.data.facilities.filter((f) => f.signals.some((s) => s.name === 'escalation' && s.status === 'unknown')).length : null;
  const floor = anomalies.data?.buildingBaseline[0]?.need ?? null;
  const o = overview.data;
  const flagRows: { k: string; n: number; tone: string }[] = [];
  if (o) {
    flagRows.push({ k: 'Scam / fraud blocked', n: o.decisions.exploitationsPrevented, tone: 'red' });
    if (o.flags) {
      flagRows.push({ k: 'Crisis escalations', n: o.flags.crisis, tone: 'slate' });
      flagRows.push({ k: 'AI checks drifting', n: o.flags.drift, tone: 'slate' });
      flagRows.push({ k: 'Caregiver review queue', n: o.flags.queue, tone: 'accent' });
    } else {
      flagRows.push({ k: 'held for a person', n: o.decisions.held, tone: 'accent' });
    }
  }
  const tail: string[] = [];
  if (off && off.length > 0) tail.push(off.map((r) => r.name).join(' · '));
  if (neverEsc !== null) tail.push(`${neverEsc} facilities have never exercised escalation, unknown, not healthy`);
  if (floor !== null) tail.push(`Under ${floor} requests in the window, unmeasured, not healthy.`);

  return (
    <section className="op-inc" aria-label="Open incidents">
      <Head title="Open incidents" small meta={q.data ? `${open.length} unresolved · ${unacked} unacknowledged · acknowledging halts escalation` : undefined} />
      <QueryBlock q={q} rows={2} isEmpty={() => open.length === 0} empty={<EmptyState compact title="No open incidents" body="Every System Pulse incident in the last 7 days is resolved." />}>
        {() => (
          <>
            {open.slice(0, 3).map((i, n) => {
              const held = i.status === 'acknowledged';
              const ageMin = (now.getTime() - Date.parse(i.openedAt)) / 60000;
              const pct = i.escalateAtMinutes ? Math.min(100, Math.round((ageMin / i.escalateAtMinutes) * 100)) : null;
              const tint = held ? 'held' : pct !== null && pct >= 80 ? 'hot' : 'warm';
              return (
                <div key={i.id} className={`op-inc__row${held ? ' op-inc__row--held' : ''}`}>
                  <i className={`op-inc__bar op-inc__bar--${tint}`} style={{ width: `${pct ?? 0}%` }} aria-hidden="true" />
                  <button
                    type="button"
                    className={`op-inc__box${held ? ' op-inc__box--on' : ''}`}
                    aria-label={held ? `${i.title}: acknowledged` : `Acknowledge ${i.title}`}
                    aria-pressed={held}
                    disabled={held || readOnly}
                    title={readOnly ? roReason : held ? 'Acknowledged' : 'Acknowledge: halts escalation'}
                    onClick={() => setAcking(i)}
                  >
                    {held ? '✓' : ''}
                  </button>
                  <span className="op-inc__rank">{n + 1}</span>
                  <span className="op-inc__text">
                    <span className="op-inc__what">{i.title}</span>
                    <span className="op-inc__note">
                      {i.escalateAtMinutes
                        ? held
                          ? `held before escalation at ${i.escalateAtMinutes}m`
                          : `${pct}% to escalation at ${i.escalateAtMinutes}m`
                        : held
                          ? `acknowledged ${fmtAgeSince(i.acknowledgedAt, now)} ago, escalation halted`
                          : i.escalationLevel > 0
                            ? `escalated to level ${i.escalationLevel} · ${i.severity}`
                            : `not escalated yet · ${i.severity}`}
                    </span>
                  </span>
                  <span className="op-inc__age">{fmtAgeSince(i.openedAt, now)}</span>
                  {held && i.acknowledgedBy && (
                    <span className="op-inc__who" title={`Acknowledged by ${ackerName(i)}`}>
                      {initials(ackerName(i))}
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </QueryBlock>
      <div className="op-flags">
        {flagRows.map((f) => (
          <span key={f.k}>
            <i className={`op-dot op-dot--${f.tone}`} />
            <b>{fmtCount(f.n)}</b>
            <span>{f.k}</span>
          </span>
        ))}
      </div>
      {(off || tail.length > 0) && (
        <DevNote>
        <div className="op-note op-inc__tail">
          {off && (
            <span className="op-strong">{`${off.length} of ${rules.data!.length} rules not paging`}</span>
          )}
          {tail.length > 0 && `${off ? ' · ' : ''}${tail.join(' · ')}`}
        </div>
        </DevNote>
      )}
      {acking && (
        <ConfirmDialog
          title="Acknowledge this incident?"
          body={
            <>
              <p style={{ margin: '0 0 6px' }}>
                <b>{acking.title}</b>
              </p>
              Acknowledging tells the on-call rota a person has it, and <b>halts escalation</b>. It is recorded against your name.
            </>
          }
          confirmLabel="Acknowledge"
          onConfirm={async () => {
            await api.pulseIncidentAck(client, { params: { id: acking.id }, body: withConfirm({}) as { confirm: true } });
            q.reload();
          }}
          onClose={() => setAcking(null)}
        />
      )}
    </section>
  );
}

// ── Facility health grid ────────────────────────────────────────────────────

export const HEALTH_COLS: { s: string; name: SignalName; full: string }[] = [
  { s: 'S', name: 'sensor', full: 'sensors, last check-in received' },
  { s: 'A', name: 'companion', full: 'Atlas, last time a resident talked with it' },
  { s: 'E', name: 'escalation', full: 'escalation, last time the path actually fired' },
  { s: 'F', name: 'data-freshness', full: 'freshness, age of the newest record' },
  { s: 'Q', name: 'data-quality', full: 'quality, rejects over receives' },
  { s: 'T', name: 'early-warning', full: 'trend, early warning against its own baseline' },
];

function cell(status: SigStatus | undefined): { mark: string; cls: string } {
  switch (status) {
    case 'ok':
      return { mark: '✓', cls: 'ok' };
    case 'degraded':
    case 'down':
      return { mark: '!', cls: 'bad' };
    case 'unknown':
      return { mark: '?', cls: 'unknown' };
    default:
      return { mark: '–', cls: 'none' };
  }
}

/** A catalog name in capitals ("THE CENTER AT PARMER") reads as the design writes it ("The Center at Parmer"). */
export function displayName(n: string): string {
  if (n !== n.toUpperCase()) return n;
  const small = new Set(['at', 'by', 'of', 'the', 'and']);
  return n
    .toLowerCase()
    .split(' ')
    .map((w, i) => (w === 'ii' ? 'II' : w === 'c' ? 'C' : i > 0 && small.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

function uptimeTone(v: number | null | undefined): string {
  return v === null || v === undefined ? 'none' : v >= 99.9 ? 'good' : 'watch';
}

export function HealthGrid({ q }: { q: QueryState<PulseFacilities> }) {
  const plain = useCustomerText();
  const served = q.data ? q.data.facilities.some((f) => f.uptimePct !== undefined) : true;
  return (
    <section className="op-health" aria-label="Facility health">
      <Head
        title="Facility health"
        meta={
          `S sensors · A Atlas · E escalation · F freshness · Q quality · T trend, ✓ passing · ! degraded · ? unknown, never counted as healthy · – not set up${
            served ? '' : plain(' · uptime not served per facility yet', ' · uptime not available yet')
          }`
        }
      />
      <QueryBlock q={q} rows={5} isEmpty={(d) => d.facilities.length === 0} empty={<EmptyState compact title="No facilities are reporting to System Pulse yet" />}>
        {(d) => (
          <div className="op-grid" role="table" aria-label="Facility health by signal">
            <div role="row" className="op-grid__row">
              <span role="columnheader" className="op-grid__name">
                <span className="sr-only">Facility</span>
              </span>
              {HEALTH_COLS.map((c) => (
                <span role="columnheader" key={c.s} title={c.full} className="op-grid__h">
                  {c.s}
                </span>
              ))}
              <span role="columnheader" className="op-grid__h op-grid__h--up">
                uptime
              </span>
            </div>
            {d.facilities.map((f: FacilityVitals) => (
              <div role="row" key={f.organizationId} className="op-grid__row">
                <Link role="rowheader" to="/pulse" className="op-grid__name" title={`${displayName(f.name)} · open in System Pulse`}>
                  {displayName(f.name)}
                </Link>
                {HEALTH_COLS.map((c) => {
                  const sig = f.signals.find((s) => s.name === c.name);
                  const v = cell(sig?.status);
                  return (
                    <span role="cell" key={c.s} className={`op-cell op-cell--${v.cls}`} title={`${displayName(f.name)} · ${c.full}: ${sig ? `${sig.status}, ${sig.detail}` : 'no program'}`}>
                      {v.mark}
                    </span>
                  );
                })}
                <span role="cell" className={`op-grid__up op-ink--${uptimeTone(f.uptimePct)}`}>
                  {f.uptimePct === null || f.uptimePct === undefined ? '–' : `${f.uptimePct}%`}
                </span>
              </div>
            ))}
          </div>
        )}
      </QueryBlock>
    </section>
  );
}

// ── Guard posture, outcomes, action layer, decision path ────────────────────

const LAYER_BG = ['#8FA9C4', '#5C7FA8', '#3E6690', '#264C7E'];

export function GuardPostureBlock({ overview, monitoring, posture }: { overview: QueryState<MetricsOverview>; monitoring: QueryState<MetricsMonitoring>; posture: QueryState<PolicyPosture> }) {
  const d = overview.data?.decisions;
  const split = d && d.flagged !== undefined && d.escalated !== undefined;
  const mix = d
    ? split
      ? [
          { k: 'allowed', n: d.approved, cls: 'allowed' },
          { k: 'flagged', n: d.flagged!, cls: 'flagged' },
          { k: 'escalated', n: d.escalated!, cls: 'escalated' },
          { k: 'halted', n: d.blocked, cls: 'halted' },
        ]
      : [
          { k: 'allowed', n: d.approved, cls: 'allowed' },
          { k: 'held', n: d.held, cls: 'flagged' },
          { k: 'halted', n: d.blocked, cls: 'halted' },
        ]
    : [];
  const total = d?.total ?? 0;
  // GOVERNED: a decision the gate recorded in the ledger (approved, held or blocked) in
  // the window. The API marks an empty window real: false ("honest empty"), which is not
  // sample data, so it reads "no gate decisions recorded", never "0 governed · SAMPLE".
  const noDecisions = !!d && total === 0;
  const w = (n: number) => (total ? `${((n / total) * 100).toFixed(1)}%` : '0%');
  const m = monitoring.data;
  const lat = m?.latency;
  const path = m?.decisionPath;
  const pathTotal = path ? path.layers.reduce((t, l) => t + (l.ms ?? 0), 0) : 0;
  const postureWord = posture.data?.posture ? posture.data.posture.charAt(0) + posture.data.posture.slice(1).toLowerCase() : null;
  return (
    <section className="op-posture" aria-label="Guard posture">
      <Head
        title="Guard posture"
        meta={
          overview.data ? (
            <>
              last {overview.data.windowDays} days{d?.real ? ' · measured' : ''}
              {!noDecisions && <HonestyBadge flags={[{ real: d?.real }]} figure="Guard posture: decisions" hide={['MEASURED']} />}
            </>
          ) : undefined
        }
      />
      <span className="op-cap op-cap--first">{noDecisions ? 'Outcomes · no gate decisions recorded' : `Outcomes · ${fmtCount(total)} governed`}</span>
      {noDecisions && <span className="op-cap" data-testid="op-outcomes-source">Counts gate decisions in the ledger, not Atlas conversations</span>}
      <QueryBlock q={overview} rows={2}>
        {() => (
          <>
            <span className="op-mix" role="img" aria-label={mix.map((x) => `${x.n} ${x.k}`).join(', ')}>
              {mix.map((x) => (
                <i key={x.k} className={`op-mix--${x.cls}`} style={{ width: w(x.n) }} />
              ))}
            </span>
            <span className="op-mix__legend">
              {mix.map((x) => (
                <span key={x.k}>
                  <i className={`op-key op-mix--${x.cls}`} />
                  <b>{fmtCount(x.n)}</b>
                  <span className="op-mix__k">{x.k}</span>
                  <span className="op-mix__w">{w(x.n)}</span>
                </span>
              ))}
            </span>
          </>
        )}
      </QueryBlock>

      <span className="op-cap">Action layer</span>
      <span className="op-act">
        <span>
          <b>{path ? fmtCount(path.reachedAct) : d ? fmtCount(d.total) : '–'}</b>
          <span>Actions gated</span>
        </span>
        <span>
          <b>{d ? fmtCount(d.blocked) : '–'}</b>
          <span>Blocked</span>
        </span>
        <span>
          <b>{m ? fmtCount(m.held.count) : '–'}</b>
          <span>Held for human</span>
        </span>
        <span>
          <b>{postureWord ?? '–'}</b>
          <span>
            Guard posture {posture.data && <HonestyBadge flags={[{ real: posture.data.real }]} figure="Guard posture word" className="ov-badge-mini" hide={['MEASURED']} />}
          </span>
        </span>
      </span>
      {posture.error && <p className="op-note">Posture could not load (request {posture.error.requestId ?? 'no id'}).</p>}

      <span className="op-cap op-cap--row">
        <Link to="/oversight" className="op-cap__link">
          Decision path
        </Link>
        <span className="op-cap__right">{path ? `${pathTotal.toFixed(1)}ms end to end` : lat && lat.p50 !== null ? `${fmtMs(lat.p50)} end to end, p50` : ''}</span>
      </span>
      {path ? (
        <>
          <Link to="/oversight" title="Open Oversight stage ladder" className="op-path">
            {path.layers.map((l, i) => (
              <i key={l.key} style={{ width: `${(((l.ms ?? 0) / (pathTotal || 1)) * 100).toFixed(1)}%`, background: LAYER_BG[i % LAYER_BG.length] }} />
            ))}
          </Link>
          <span className="op-path__legend">
            {path.layers.map((l, i) => (
              <span key={l.key}>
                <i className="op-key" style={{ background: LAYER_BG[i % LAYER_BG.length] }} />
                <b title={l.reason ?? undefined}>{l.ms === null ? '–' : `${l.ms}ms`}</b>
                <span className="op-path__k">{l.name}</span>
                <span className="op-path__pct">{l.passPct === null ? '–' : `${l.passPct}%`}</span>
              </span>
            ))}
          </span>
        </>
      ) : lat ? (
        <p className="op-note">
          p50 {fmtMs(lat.p50)} {'·'} p90 {fmtMs(lat.p90)} {'·'} p99 {fmtMs(lat.p99)} over {fmtCount(lat.count)} decisions{' '}
          <HonestyBadge flags={[{ real: lat.real }]} figure="Decision path latency" className="ov-badge-mini" hide={['MEASURED']} /> {'·'} per-layer timing (L1 Listen, L2 Assess, L3 Govern, L4 Act) is not aggregated by any route yet
        </p>
      ) : monitoring.loading ? (
        <Skeleton rows={1} />
      ) : null}
    </section>
  );
}

// ── Against own baseline ────────────────────────────────────────────────────

function BaseLine({ k, cur, base, curW, baseW, bad, was = true }: { k: string; cur: string; base: string; curW: number; baseW: number; bad: boolean; was?: boolean }) {
  return (
    <span className="op-base__line">
      <span className="op-base__k">{k}</span>
      <span className="op-base__bars">
        <span className={`op-base__cur${bad ? ' op-base__cur--bad' : ''}`} style={{ width: `${curW}%` }} />
        <span className="op-base__was" style={{ width: `${baseW}%` }} />
      </span>
      <b>{cur}</b>
      <span className="op-base__base">{was ? `was ${base}` : base}</span>
    </span>
  );
}

function pair(cur: number, base: number, fmt: (v: number) => string) {
  const top = Math.max(cur, base) * 1.35 || 1;
  return { cur: fmt(cur), base: fmt(base), curW: Math.round((cur / top) * 100), baseW: Math.round((base / top) * 100), bad: cur > base * 1.5 };
}

const secs = (ms: number) => `${Math.round(ms / 100) / 10}s`;

export function BaselineBlock({ q, facilities }: { q: QueryState<PulseAnomalies>; facilities?: PulseFacilities | null }) {
  // The back end labels facility anomalies with the org id, so the name comes from System Pulse.
  const names = new Map((facilities?.facilities ?? []).map((f) => [f.organizationId, f.name]));
  // Never a raw id on screen (the role crawl found one). The name comes from System Pulse's
  // facility list, else the anomaly report's own names (baselineNames, or the row's label);
  // a building with no name on file says so plainly.
  const nameOf = (id: string, label?: string | null) => names.get(id) ?? q.data?.baselineNames?.[id] ?? (label && label !== id ? label : 'Building name not on file');
  const quiet = facilities ? facilities.facilities.filter((f) => f.status === 'unknown' || f.status === 'n/a').length : null;
  return (
    <section className="op-baseline" aria-label="Against own baseline">
      <Head
        title="Against own baseline"
        meta="Each facility is judged against its OWN baseline, not the platform average, one building breaking is caught while the aggregate stays green."
      />
      <QueryBlock q={q} rows={3}>
        {(d) => {
          const building = d.buildingBaseline.filter((b) => b.scope === 'facility');
          if (d.baselines) {
            const plat = d.platformRejectPct ?? null;
            const rows = [...d.baselines].sort((a, b) => (a.state === 'flagged' ? 0 : 1) - (b.state === 'flagged' ? 0 : 1));
            return (
              <>
                {rows.map((b) => {
                  const err = pair(b.errorPct, b.errorBaselinePct, (v) => `${v}%`);
                  const p95 = pair(b.p95Ms, b.p95BaselineMs, secs);
                  const rate = b.receives ? (b.rejects / b.receives) * 100 : 0;
                  const top = Math.max(rate, plat ?? 0) * 1.35 || 1;
                  return (
                    <div key={`base:${b.id}`} className="op-base">
                      <Link to="/performance" title="Open in Benchmark" className="op-base__name">
                        {nameOf(b.id, building.find((x) => x.id === b.id)?.label)}
                      </Link>
                      <BaseLine k="error" {...err} />
                      <BaseLine k="p95" {...p95} />
                      <BaseLine
                        k="rejects"
                        cur={`${rate.toFixed(1)}%`}
                        base={`${fmtCount(b.rejects)} of ${fmtCount(b.receives)}`}
                        curW={Math.round((rate / top) * 100)}
                        baseW={plat === null ? 0 : Math.round((plat / top) * 100)}
                        bad={plat !== null && rate > plat}
                        was={false}
                      />
                    </div>
                  );
                })}
                {/* A building can be in both lists (its baselines, and a building-level anomaly). */}
                {building.map((b, i) => (
                  <div key={`building:${b.id}:${i}`} className="op-base">
                    <Link to="/performance" title="Open in Benchmark" className="op-base__name">
                      {nameOf(b.id, b.label)}
                    </Link>
                    <span className="op-base__note">{b.detail}</span>
                  </div>
                ))}
                <span className="op-base__rest">
                  {quiet !== null && `${quiet} facilities have no requests in the window, nothing to judge yet`}
                  {plat !== null && ` · grey bar = platform reject rate ${plat}%`}
                </span>
              </>
            );
          }
          const fac = d.anomalies.filter((a) => a.scope === 'facility');
          const byId = new Map<string, typeof fac>();
          fac.forEach((a) => byId.set(a.id, [...(byId.get(a.id) ?? []), a]));
          return (
            <>
              {byId.size === 0 && <p className="op-note">No facility is off its own baseline right now.</p>}
              {[...byId.entries()].slice(0, 2).map(([id, list]) => (
                <div key={id} className="op-base">
                  <span className="op-base__name">{nameOf(id, list[0].label)}</span>
                  {list.map((a) => {
                    const lat = a.signal === 'latency';
                    const p = pair(a.observed, a.baseline, lat ? secs : (v) => `${v.toFixed(1)}%`);
                    return <BaseLine key={a.signal} k={lat ? 'p95' : a.signal === 'error-rate' ? 'error' : a.signal} {...p} bad={a.severity === 'high'} />;
                  })}
                </div>
              ))}
              {building.length > 0 && (
                <p className="op-note">
                  {building.length} facilities are still building a baseline ({building.map((b) => `${nameOf(b.id, b.label)} ${b.have} of ${b.need}`).slice(0, 3).join(', ')}), so not judged yet
                </p>
              )}
            </>
          );
        }}
      </QueryBlock>
    </section>
  );
}

// ── Objectives ──────────────────────────────────────────────────────────────

const SIGNAL_NAME: Record<string, string> = {
  companion: 'Atlas availability',
  'companion-availability': 'Atlas availability',
  'read-latency': 'page load speed',
  'runtime-p99': 'slowest responses',
  'facility-ingest': 'building data arriving',
};

const signalName = (s: string) => SIGNAL_NAME[s] ?? s.replace(/-/g, ' ');

export function ObjectivesRow({ q }: { q: QueryState<PulseSlo> }) {
  const set = q.data?.slos.filter((s) => s.targetPct !== null) ?? [];
  const unset = q.data?.slos.filter((s) => s.targetPct === null) ?? [];
  return (
    <section className="op-objectives" aria-label="Objectives">
      <span className="op-objectives__k">Objectives</span>
      {q.loading && !q.data && <span className="op-muted">Loading{'…'}</span>}
      {q.error && !q.data && <span className="op-muted">Objectives could not load (request {q.error.requestId ?? 'no id'}).</span>}
      {set.map((s) => {
        const att = s.attainedPct;
        const target = s.targetPct as number;
        const cls = att === null ? 'none' : att >= target ? 'good' : att >= target - 15 ? 'watch' : 'act';
        return (
          <span key={`${s.scope}:${s.scopeId}:${s.signal}`} className="op-objectives__o" title={s.detail}>
            <b className={`op-ink--${cls}`}>{att === null ? '–' : `${Math.round(att)}%`}</b>
            <span>{signalName(s.signal)}</span>
            <span className={s.burn24h !== null && s.burn24h > 1 ? 'op-ink--act' : 'op-muted'}>{s.burn24h === null ? 'not measured' : `${Number(s.burn24h.toFixed(1))}× its allowed misses`}</span>
          </span>
        );
      })}
      {q.data && q.data.slos.length === 0 && <span className="op-muted">No objectives are set yet.</span>}
      <span className="op-objectives__note">
        {`Objectives are ${set[0]?.windowDays ?? 30}-day; only the recorded window is measured.${unset
          .map((s) => ` ${signalName(s.signal).charAt(0).toUpperCase()}${signalName(s.signal).slice(1)} has no objective yet.`)
          .join('')}`}
      </span>
    </section>
  );
}
